<template>
  <v-dialog v-model="isOpen" max-width="760" persistent scrollable attach="body">
    <v-card class="support-dialog" @paste.capture="handlePaste">
      <v-card-title class="support-dialog__title">
        <div>
          <strong>Acionar suporte</strong>
          <span>{{ context.sistema }}</span>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="support-dialog__body">
        <div v-if="step === 'context'">
          <p class="support-dialog__lead">
            Identificamos que voce esta acessando:
          </p>
          <SupportContextSummary :context="context" class="mb-4" />

          <p class="support-dialog__question">
            Esse e o ambiente onde voce precisa de ajuda?
          </p>
          <v-radio-group v-model="contextOk" dense>
            <v-radio label="Sim, esta correto" :value="true"></v-radio>
            <v-radio label="Nao, parece estar errado" :value="false"></v-radio>
          </v-radio-group>

          <v-textarea
            v-if="contextOk === false"
            v-model="contextExpected"
            outlined
            dense
            rows="3"
            label="O que voce esperava acessar?"
            :placeholder="contextExpectedPlaceholder"
          ></v-textarea>

          <SupportAgentCard
            v-if="contextOk === false && (contextAnalysisLoading || contextAnalysis)"
            :analysis="contextAnalysis"
            :loading="contextAnalysisLoading && !contextAnalysis"
          />

          <v-textarea
            v-if="contextOk === false && contextAnalysis"
            v-model="contextFollowup"
            outlined
            dense
            rows="2"
            label="O que aconteceu depois?"
            placeholder="Ex.: abri o link e apareceu senha incorreta; nao recebi o codigo; meu e-mail nao foi reconhecido."
          ></v-textarea>
        </div>

        <div v-if="step === 'describe'">
          <SupportContextSummary :context="context" class="mb-4" />
          <v-textarea
            v-model="description"
            outlined
            rows="4"
            label="Descreva em poucas palavras o que esta acontecendo"
            :placeholder="placeholder"
          ></v-textarea>

          <v-text-field
            v-if="!context.logado"
            v-model="emailInformado"
            outlined
            dense
            label="E-mail usado para acessar, se for relevante"
          ></v-text-field>

          <v-text-field
            v-model="errorMessage"
            outlined
            dense
            label="Mensagem de erro, se houver"
          ></v-text-field>

          <v-file-input
            v-model="files"
            outlined
            dense
            multiple
            counter
            show-size
            accept="image/png,image/jpeg,image/webp,application/pdf"
            prepend-icon="mdi-paperclip"
            label="Print ou anexo"
            hint="Selecione um arquivo ou cole uma imagem printada com Ctrl+V nesta tela."
            persistent-hint
          ></v-file-input>
        </div>

        <div v-if="step === 'result'">
          <SupportAgentCard :analysis="analysis" />

          <v-alert
            v-if="analysis.precisaInformacaoAdicional"
            type="warning"
            outlined
            dense
          >
            Para reduzir novas trocas, informe:
            {{ analysis.informacoesNecessarias.join(", ") }}.
          </v-alert>

          <v-textarea
            v-model="complement"
            outlined
            dense
            rows="3"
            label="Complementar com mais informacoes"
          ></v-textarea>
        </div>

        <div v-if="step === 'review'">
          <v-alert type="warning" outlined dense>
            Revise os dados abaixo. O contexto, descricao, analise e anexos
            serao enviados ao suporte.
          </v-alert>

          <SupportContextSummary :context="context" class="mb-4" />

          <div class="support-review">
            <p><strong>Categoria:</strong> {{ analysis.categoriaLabel }}</p>
            <p><strong>Descricao:</strong> {{ description }}</p>
            <p v-if="errorMessage">
              <strong>Mensagem de erro:</strong> {{ errorMessage }}
            </p>
            <p v-if="complement">
              <strong>Complemento:</strong> {{ complement }}
            </p>
            <p>
              <strong>Anexos:</strong>
              {{ files && files.length ? files.length : "nenhum" }}
            </p>
          </div>

          <v-textarea
            v-model="notResolvedReason"
            outlined
            dense
            rows="3"
            label="Por que a orientacao nao resolveu?"
          ></v-textarea>
        </div>

        <div v-if="step === 'sent'">
          <v-alert type="success" outlined>
            Solicitacao enviada ao suporte.
            <div v-if="protocol" class="mt-2">
              Protocolo: <strong>{{ protocol }}</strong>
            </div>
          </v-alert>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="support-dialog__actions">
        <v-btn text @click="back" v-if="canBack && !loading">Voltar</v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="close" v-if="step === 'sent'">Fechar</v-btn>
        <v-btn
          class="support-dialog__primary"
          :loading="loading"
          :disabled="primaryDisabled"
          @click="next"
          v-else
        >
          {{ actionLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SupportContextSummary from "./SupportContextSummary.vue";
import SupportAgentCard from "./SupportAgentCard.vue";
import {
  buildSupportContext,
  getContextExpectedPlaceholder,
  getPlaceholder,
} from "../services/supportContext";
import { suporteApi } from "../services/supportApi";

export default {
  name: "SupportDialog",
  components: {
    SupportContextSummary,
    SupportAgentCard,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    origin: {
      type: String,
      default: "area_logada",
    },
    initialContext: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      step: "context",
      context: {},
      contextOk: true,
      contextExpected: "",
      description: "",
      errorMessage: "",
      emailInformado: "",
      files: [],
      analysis: {},
      complement: "",
      notResolvedReason: "",
      protocol: "",
      loading: false,
      contextAnalysis: null,
      contextAnalysisLoading: false,
      contextAnalysisTimer: null,
      lastContextExpectedAnalyzed: "",
      contextFollowup: "",
    };
  },
  computed: {
    placeholder() {
      return getPlaceholder(this.context || {});
    },
    contextExpectedPlaceholder() {
      return getContextExpectedPlaceholder(this.context || {});
    },
    primaryDisabled() {
      if (this.loading) {
        return true;
      }

      if (this.step === "context") {
        return this.contextOk === false && !this.contextExpected.trim();
      }

      if (this.step === "describe") {
        return !this.description.trim() && this.contextOk !== false;
      }

      return false;
    },
    canBack() {
      return ["describe", "result", "review"].includes(this.step);
    },
    actionLabel() {
      if (this.step === "context") return "Continuar";
      if (this.step === "describe") return "Analisar";
      if (this.step === "result") return "Enviar para o suporte";
      if (this.step === "review") return "Enviar";
      return "Continuar";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.isOpen = value;
        if (value) {
          this.reset();
        }
      },
    },
    isOpen(value) {
      this.$emit("input", value);
    },
    contextOk(value) {
      if (value !== false) {
        this.contextAnalysis = null;
        this.contextAnalysisLoading = false;
      } else {
        this.scheduleContextAnalysis();
      }
    },
    contextExpected() {
      this.scheduleContextAnalysis();
    },
    contextFollowup() {
      this.scheduleContextAnalysis();
    },
  },
  beforeDestroy() {
    if (this.contextAnalysisTimer) {
      clearTimeout(this.contextAnalysisTimer);
    }
  },
  methods: {
    reset() {
      this.step = "context";
      this.context = this.initialContext || buildSupportContext(this, this.origin);
      this.contextOk = true;
      this.contextExpected = "";
      this.description = "";
      this.errorMessage = "";
      this.emailInformado = "";
      this.files = [];
      this.analysis = {};
      this.complement = "";
      this.notResolvedReason = "";
      this.protocol = "";
      this.loading = false;
      this.contextAnalysis = null;
      this.contextAnalysisLoading = false;
      this.lastContextExpectedAnalyzed = "";
      this.contextFollowup = "";
    },
    close() {
      this.isOpen = false;
    },
    back() {
      if (this.step === "describe") this.step = "context";
      else if (this.step === "result") this.step = "describe";
      else if (this.step === "review") this.step = "result";
    },
    next() {
      if (this.step === "context") {
        this.step = "describe";
        return;
      }

      if (this.step === "describe") {
        this.analyze();
        return;
      }

      if (this.step === "result") {
        this.step = "review";
        return;
      }

      if (this.step === "review") {
        this.send();
      }
    },
    buildAnalysisPayload() {
      return {
        contexto: this.context,
        contextoCorreto: this.contextOk,
        contextoEsperado: this.contextExpected,
        descricao: this.description,
        mensagemErro: this.errorMessage,
        emailInformado: this.emailInformado,
        temAnexo: !!(this.files && this.files.length),
        nomesAnexos: (this.files || []).map((file) => file.name),
        complementos: [this.contextFollowup, this.complement].filter(Boolean),
        etapaAssistente: this.context.logado ? "area_logada" : "nao_logado",
        historicoAssistente: this.buildAssistantHistory(),
      };
    },
    scheduleContextAnalysis() {
      if (this.contextOk !== false) {
        return;
      }

      if (this.contextAnalysisTimer) {
        clearTimeout(this.contextAnalysisTimer);
      }

      this.contextAnalysisTimer = setTimeout(() => {
        this.analyzeContextExpected();
      }, 800);
    },
    analyzeContextExpected() {
      const expected = this.contextExpected.trim();
      const followup = this.contextFollowup.trim();
      const analysisKey = `${expected}|${followup}`;
      if (
        (expected.length < 4 && followup.length < 4) ||
        analysisKey === this.lastContextExpectedAnalyzed
      ) {
        return;
      }

      this.lastContextExpectedAnalyzed = analysisKey;
      this.contextAnalysisLoading = true;

      suporteApi
        .analisar({
          contexto: this.context,
          contextoCorreto: false,
          contextoEsperado: expected,
          descricao: followup
            ? `Ambiente esperado: ${expected}. Retorno do usuario: ${followup}`
            : `Ambiente esperado: ${expected}`,
          temAnexo: !!(this.files && this.files.length),
          nomesAnexos: (this.files || []).map((file) => file.name),
          complementos: followup ? [followup] : [],
          etapaAssistente: "contexto_nao_logado",
          historicoAssistente: this.buildAssistantHistory(),
          registrarAtendimentoInicial: true,
        })
        .then(({ data }) => {
          this.contextAnalysis = data || null;
          if (!this.analysis || !this.analysis.categoria) {
            this.analysis = data || {};
          }
        })
        .catch(() => {
          this.contextAnalysis = {
            categoriaLabel: "ambiente divergente",
            causaProvavel:
              "Pode haver divergencia entre UF, ciclo, URL, perfil ou ambiente esperado.",
            confianca: "baixa",
            orientacao: [
              "Confirme se o link acessado corresponde ao ambiente esperado.",
              "Se souber, informe a UF e o municipio corretos antes de enviar ao suporte.",
            ],
            assistenteTitulo: "Nao consegui analisar agora",
            assistenteMensagem:
              "Mesmo assim, podemos seguir. Confirme se o link aberto corresponde a UF, ciclo e tipo de acesso esperado.",
            assistentePergunta:
              "Qual UF e tipo de acesso voce queria abrir?",
          };
        })
        .finally(() => {
          this.contextAnalysisLoading = false;
        });
    },
    analyze() {
      if (!this.description && this.contextOk !== false) {
        this.$notification.error("Descreva o problema antes de continuar.", {
          timer: 5,
        });
        return;
      }

      this.loading = true;
      suporteApi
        .analisar(this.buildAnalysisPayload())
        .then(({ data }) => {
          this.analysis = data || {};
          this.step = "result";
        })
        .catch(() => {
          this.analysis = {
            categoria: "outro",
            categoriaLabel: "outro",
            causaProvavel:
              "Nao foi possivel analisar automaticamente. O caso pode ser enviado ao suporte com os dados preenchidos.",
            confianca: "baixa",
            orientacao: [
              "Revise o contexto detectado e envie a solicitacao ao suporte.",
            ],
            precisaInformacaoAdicional: false,
            informacoesNecessarias: [],
            precisaPrint: false,
            deveEscalar: true,
            resumoCurto: this.description || "acionamento de suporte",
          };
          this.step = "result";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    send() {
      this.loading = true;
      suporteApi
        .enviar(
          {
            contexto: this.context,
            contextoCorreto: this.contextOk,
            contextoEsperado: this.contextExpected,
            descricao: this.description,
            mensagemErro: this.errorMessage,
            emailInformado: this.emailInformado,
            motivoNaoResolvido: this.notResolvedReason,
            complemento: this.complement,
            analise: this.analysis,
          },
          this.files || []
        )
        .then(({ data }) => {
          this.protocol = data && data.protocolo;
          this.step = "sent";
        })
        .catch((err) => {
          const message =
            (err.response && err.response.data) ||
            "Nao foi possivel enviar a solicitacao.";
          this.$notification.error(message, { timer: 8 });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handlePaste(event) {
      const clipboard = event.clipboardData;
      if (!clipboard || !clipboard.items) {
        return;
      }

      const pastedFiles = [];
      Array.from(clipboard.items).forEach((item) => {
        if (item.kind !== "file" || item.type.indexOf("image/") !== 0) {
          return;
        }

        const file = item.getAsFile();
        if (!file) {
          return;
        }

        const extension = file.type === "image/jpeg" ? "jpg" : "png";
        pastedFiles.push(
          new File([file], `print-colado-${Date.now()}.${extension}`, {
            type: file.type || "image/png",
          })
        );
      });

      if (!pastedFiles.length) {
        return;
      }

      event.preventDefault();
      this.files = [...(this.files || []), ...pastedFiles].slice(0, 5);

      if (this.$notification) {
        this.$notification.success("Print colado nos anexos.", { timer: 4 });
      }
    },
    buildAssistantHistory() {
      return [
        this.contextExpected
          ? {
              papel: "usuario",
              texto: this.contextExpected,
              momento: "ambiente_esperado",
            }
          : null,
        this.contextFollowup
          ? {
              papel: "usuario",
              texto: this.contextFollowup,
              momento: "retorno_apos_orientacao",
            }
          : null,
      ].filter(Boolean);
    },
  },
};
</script>

<style lang="scss" scoped>
.support-dialog__title {
  display: flex;
  align-items: center;
  justify-content: space-between;

  div {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  strong {
    font-size: 18px;
    line-height: 1.2;
  }

  span {
    color: #667085;
    font-size: 13px;
    margin-top: 2px;
  }
}

.support-dialog__body {
  padding-top: 18px !important;
}

.support-dialog__lead,
.support-dialog__question {
  color: #344054;
  font-size: 14px;
}

.support-dialog__question {
  font-weight: 600;
  margin-top: 16px;
}

.support-dialog__steps {
  padding-left: 22px;

  li {
    margin-bottom: 8px;
  }
}

.support-dialog__actions {
  padding: 12px 20px;
}

.support-dialog__primary {
  background: #1f2937 !important;
  color: #fff !important;
  min-width: 130px;

  &.v-btn--disabled {
    background: #d1d5db !important;
    color: #4b5563 !important;
  }
}

.support-dialog__context-analysis {
  margin-top: 12px;

  p {
    color: #344054;
    font-size: 13px;
    margin-bottom: 8px;
  }
}

.support-review {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;

  p {
    margin-bottom: 8px;
    overflow-wrap: anywhere;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
